<template>
  <div class="protocol-view">
    <v-toolbar
      v-if="protocol && !inlineMode"
      class="fixed-bar"
    >
      <v-btn
        v-if="!inlineMode"
        class="mr-3"
        title="Back to Standardizer Home"
        @click="$router.push('/stdizer')"
        v-b-tooltip
        aria-label="Back to Standardizer Home"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        hide-details
        label="Search..."
        single-line
        class="mr-5"
        style="min-width: 10em; max-width: 15em"
        :title="`Search in '${protocol.name}' fields (DTXCID, DTXSID, Name, CASRN, InChI and InChIKey)`"
        v-b-tooltip
      />

      <b-form-select
        v-model="issue"
        :disabled="issuesLoading"
        title="Filter by Issue"
        :options="issues"
        v-b-tooltip
        class="mx-2"
        style="min-width: 5em; max-width: fit-content"
      />
      <b-form-select
        v-model="change"
        :disabled="changesLoading"
        title="Filter by Change"
        :options="changes"
        v-b-tooltip
        class="mx-2"
        style="min-width: 5em; max-width: fit-content"
      />

      <b-form-select
        v-model="recordIdField"
        title="Record ID field"
        :options="recordIdOptions"
        v-b-tooltip
        class="mx-2"
        style="min-width: 5em; max-width: fit-content"
      />

      <v-checkbox
        v-if="countHidden"
        v-model="hidden"
        class="mr-2 mt-5"
        title="Hidden"
        v-b-tooltip
      >
        <template #label>
          <v-badge
            :content="countHidden"
            overlap
            class="small"
            offset-y="6"
          >
            <v-icon>mdi-file-hidden</v-icon>
          </v-badge>
        </template>
      </v-checkbox>

      <v-checkbox
        v-if="countComponents"
        v-model="components"
        class="mr-2 mt-5"
        title="Component"
        v-b-tooltip
      >
        <template #label>
          <v-badge
            :content="countComponents"
            overlap
            class="small"
            offset-y="6"
          >
            <v-icon>mdi-hexagon-outline</v-icon>
          </v-badge>
        </template>
      </v-checkbox>

      <v-checkbox
        v-if="countChanged"
        v-model="changed"
        class="mr-2 mt-5"
        title="Changed"
        v-b-tooltip
      >
        <template #label>
          <v-badge
            :content="countChanged"
            overlap
            class="small"
            offset-y="6"
          >
            <v-icon>mdi-file-arrow-left-right-outline</v-icon>
          </v-badge>
        </template>
      </v-checkbox>

      <v-checkbox
        v-if="countFailed"
        v-model="failed"
        class="mr-2 mt-5"
        title="Failed"
        v-b-tooltip
      >
        <template #label>
          <v-badge
            :content="countFailed"
            overlap
            class="small"
            offset-y="6"
          >
            <v-icon color="red">mdi-file-alert-outline</v-icon>
          </v-badge>
        </template>
      </v-checkbox>

      <v-checkbox
        v-if="countPassed"
        v-model="passed"
        class="mr-2 mt-5"
        title="Passed"
        v-b-tooltip
      >
        <template #label>
          <v-badge
            :content="countPassed"
            overlap
            class="small"
            offset-y="6"
          >
            <v-icon color="green">mdi-file-check-outline</v-icon>
          </v-badge>
        </template>
      </v-checkbox>

      <v-spacer />

      <template v-if="curateMode">
        <v-checkbox
          v-model="curationFilter"
          v-for="status in statuses"
          :key="status.value"
          :value="status.value"
          :title="status.text"
          class="mr-3 mt-5"
          @change="statusFilterChanged"
        >
          <template #label>
            <span class="mt-2">
              <v-icon
                v-if="status.value === 'CORRECT'"
                :color="status.color"
                title="Correct"
                v-b-tooltip
              >
                mdi-thumb-up-outline
              </v-icon>
              <v-icon
                v-else-if="status.value === 'QUESTIONABLE'"
                :color="status.color"
                title="Questionable"
                v-b-tooltip
              >
                mdi-head-question-outline
              </v-icon>
              <v-icon
                v-else-if="status.value === 'INCORRECT'"
                :color="status.color"
                title="Incorrect"
                v-b-tooltip
              >
                mdi-thumb-down-outline
              </v-icon>
              <v-icon
                v-else
                title="Unmarked"
                v-b-tooltip
              >
                mdi-circle-off-outline
              </v-icon>
            </span>
          </template>
        </v-checkbox>
      </template>

      <v-spacer />

      <v-switch
        v-if="isAuthenticated"
        v-model="curateMode"
        class="mt-5 mr-4"
        label="Curate"
        title="Show/hide curation tools"
      />

      <v-switch
        v-if="isAuthenticated && curateMode"
        v-model="selectable"
        class="mt-5 mr-4"
        label="Select"
        title="Group curation"
      />

      <v-switch
        v-model="showDetails"
        class="mt-5 mr-4"
        label="Details"
        title="Show/hide additional information"
      />

      <v-btn-toggle dense>
        <CurateDialog
          v-if="protocol"
          v-model="visibleCurate"
          :visible="selectable"
          :disabled="selected.length === 0"
          :items="selected"
          :protocol="protocol"
          @updated="updateItems"
        />
      </v-btn-toggle>

      <v-btn-toggle
        class="ml-3"
        dense
      >
        <OptionsDropdown
          :items="stdizerExportItems"
          :loading="downloading"
          title="Export to SDF, Excel, Word or PDF"
          :disabled="!filteredItems.length"
        />
      </v-btn-toggle>

      <v-btn-toggle
        class="ml-3"
        dense
      >
        <SaveDialog
          v-if="isAuthenticated"
          :api-url="`${STDIZER_URL}/protocols`"
          :button-title="protocol.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
          :item="protocol"
          :title="protocol.id ? `Edit '${protocol.name}' protocol` : 'Save protocol'"
          @itemCreated="onProtocolCreated"
          @itemUpdated="onProtocolUpdated"
        />

        <ACLDialog
          v-if="protocol.id && canACL(protocol)"
          :item="protocol"
          type="protocols"
          :api-url="STDIZER_URL"
          button-title="mdi-account-key"
          title="Access Control"
          @aclUpdated="onAclUpdated"
        />

        <PromptDialog
          v-if="canDelete(protocol)"
          button-title="mdi-delete-outline"
          :text="protocol.id ? `Delete '${protocol.name}' protocol` : 'Do you want to clear protocol?'"
          :title="protocol.id ? `Delete '${protocol.name}' protocol` : 'Clear protocol'"
          @confirmed="onReset"
        />
      </v-btn-toggle>
    </v-toolbar>

    <v-data-table
      v-if="protocol?.records?.length"
      v-model="selected"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, 100],
      }"
      :headers="headers"
      :hide-default-footer="protocolId ? protocol.filteredNumber <= 10 : filteredItems.length <= 10"
      :items="filteredItems"
      :items-per-page="10"
      :loading="loading"
      :server-items-length="protocolId ? protocol.filteredNumber : -1"
      :show-select="selectable"
      item-key="key"
      :options.sync="options"
      class="h-100 d-flex flex-column"
      fixed-header
    >
      <template #[`item.rid`]="{ item }">
        {{ item.original.chemical[recordIdField] }}
      </template>

      <template #[`item.parent`]="{ item }">
        <v-icon
          v-if="item.parent"
          title="Component record"
          v-b-tooltip
        >
          mdi-hexagon-outline
        </v-icon>
      </template>

      <template #[`item.type`]="{ item }">
        <v-icon
          v-if="item.type === 'PARENT'"
          title="Parent record"
          v-b-tooltip
        >
          mdi-hexagon-multiple-outline
        </v-icon>
        <v-icon
          v-else-if="item.type === 'DUPLICATE'"
          title="Duplicate record"
          v-b-tooltip
        >
          mdi-content-duplicate
        </v-icon>
      </template>

      <template #[`item.status`]="{ item }">
        <v-icon
          v-if="item.status === 'PASSED'"
          title="Passed"
          color="green"
          v-b-tooltip
        >
          mdi-check-circle-outline
        </v-icon>
        <v-icon
          v-if="item.status === 'FAILED'"
          title="Failed"
          color="red"
          v-b-tooltip
        >
          mdi-alert-circle-outline
        </v-icon>
      </template>

      <template #[`item.original`]="{ item }">
        <StdizerChemical
          :show-details="showDetails"
          :chemical="item.original.chemical"
          :api-url="STDIZER_URL"
          source="stdizer"
          :on-click="item.parent ? showTransformLog(item) : null"
        />
      </template>

      <template #[`item.changed`]="{ item }">
        <StdizerChemical
          v-if="item.transformLog.length"
          :show-details="showDetails"
          :chemical="item.transformLog[item.transformLog.length - 1].chemical"
          :api-url="STDIZER_URL"
          source="stdizer"
          :on-click="showTransformLog(item)"
        />
      </template>

      <template #[`item.comment`]="{ item }">
        <div style="white-space: pre">
          {{ item.comment }}
        </div>
      </template>

      <template #[`item.issues`]="{ item }">
        <div
          v-for="(protocolIssue, i) in item.issues"
          :key="`${item.ordinal}-${i}`"
        >
          {{ protocolIssue.message }}
        </div>
      </template>

      <template #[`item.curationStatus`]="{ item }">
        <v-icon
          v-if="item.curationStatus === 'CORRECT'"
          title="Correct"
          color="green"
          large
          v-b-tooltip
        >
          mdi-thumb-up-outline
        </v-icon>
        <v-icon
          v-else-if="item.curationStatus === 'QUESTIONABLE'"
          title="Questionable"
          color="yellow darken-2"
          large
          v-b-tooltip
        >
          mdi-head-question-outline
        </v-icon>
        <v-icon
          v-else-if="item.curationStatus === 'INCORRECT'"
          title="Incorrect"
          color="red"
          large
          v-b-tooltip
        >
          mdi-thumb-down-outline
        </v-icon>
      </template>

      <template #[`item.actions`]="{ item }">
        <div>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Comment"
            @click="commentRecords([item])"
            v-b-tooltip
          >
            mdi-comment-edit-outline
          </v-icon>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Reset"
            @click="uncommentRecords([item])"
            v-b-tooltip
          >
            mdi-comment-remove-outline
          </v-icon>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Correct"
            @click="markRecords([item], 'CORRECT')"
            color="green"
          >
            mdi-thumb-up-outline
          </v-icon>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Questionable"
            @click="markRecords([item], 'QUESTIONABLE')"
            color="yellow darken-2"
            v-b-tooltip
          >
            mdi-head-question-outline
          </v-icon>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Incorrect"
            @click="markRecords([item], 'INCORRECT')"
            color="red"
            v-b-tooltip
          >
            mdi-thumb-down-outline
          </v-icon>
          <v-icon
            :disabled="selectable"
            class="ma-1"
            title="Clear"
            @click="markRecords([item])"
            v-b-tooltip
          >
            mdi-circle-off-outline
          </v-icon>
        </div>
      </template>
    </v-data-table>

    <template v-else>
      <div
        v-if="!loading"
        class="mt-15 text-center"
      >
        No data to display
      </div>
    </template>

    <b-modal
      v-model="transformLogVisible"
      hide-header
      hide-footer
      size="lg"
    >
      <TransformLog
        v-model="transformLog"
        :show-details="showDetails"
      />
    </b-modal>
  </div>
</template>

<script>
import { download, getDatedFilename, STDIZER_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import CurateDialog from './CurateDialog'
import TransformLog from '@/components/stdizer/TransformLog'
import StdizerChemical from '@/components/stdizer/StdizerChemical'
import { mapGetters } from 'vuex'
import AuthBase from '@/components/auth/AuthBase'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import ACLDialog from '@/components/auth/ACLDialog'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import { debounce } from 'lodash-es'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import { stdizerAppearancePreference } from '@/components/stdizer/stdizerPreferences'

export default {
  mixins: [AuthBase],

  props: {
    inlineMode: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    PromptDialog,
    ACLDialog,
    SaveDialog,
    StdizerChemical,
    CurateDialog,
    TransformLog,
    OptionsDropdown,
  },

  data: () => ({
    search: null,
    curationFilter: [],
    statuses: [
      { text: 'No status', value: 'NONE' },
      { text: 'Correct', value: 'CORRECT', color: 'green' },
      { text: 'Incorrect', value: 'INCORRECT', color: 'red' },
      { text: 'Questionable', value: 'QUESTIONABLE', color: 'yellow darken-2' },
    ],
    visibleCurate: false,
    selected: [],
    selectable: false,
    protocol: {
      records: [],
      request: {},
    },
    transformLogVisible: false,
    transformLog: null,
    maxKey: 0,
    curateMode: false,
    options: {},
    loading: false,
    filters: {},
    issues: [],
    issue: null,
    issuesLoading: false,
    changes: [],
    change: null,
    changesLoading: false,
    recordIdOptions: [
      { value: 'cid', text: 'DTXCID' },
      { value: 'sid', text: 'DTXSID' },
      { value: 'name', text: 'Name' },
      { value: 'casrn', text: 'CASRN' },
      { value: 'smiles', text: 'SMILES' },
      { value: 'inchi', text: 'InChI' },
      { value: 'inchiKey', text: 'InChIKey' },
    ],
    downloading: false,
  }),

  computed: {
    showDetails: {
      get() {
        return stdizerAppearancePreference.value.showDetails
      },
      set(value) {
        stdizerAppearancePreference.value = {
          ...stdizerAppearancePreference.value,
          showDetails: Boolean(value),
        }
      },
    },
    recordIdField: {
      get() {
        return stdizerAppearancePreference.value.recordIdField
      },
      set(value) {
        stdizerAppearancePreference.value = {
          ...stdizerAppearancePreference.value,
          recordIdField: value,
        }
      },
    },
    page: {
      get() {
        return this.$route.query.page || this.options.page
      },
      set(val) {
        this.filters.page = val
        this.push()
      },
    },
    size: {
      get() {
        return this.$route.query.size || this.options.itemsPerPage
      },
      set(val) {
        this.filters.size = val
        this.push()
      },
    },
    hidden: {
      get() {
        return this.$route.query.hidden
      },
      set(val) {
        this.filters.hidden = val
        this.push()
      },
    },
    components: {
      get() {
        return this.$route.query.components
      },
      set(val) {
        this.filters.components = val
        this.push()
      },
    },
    changed: {
      get() {
        return this.$route.query.changed
      },
      set(val) {
        this.filters.changed = val
        this.push()
      },
    },
    passed: {
      get() {
        return this.$route.query.passed
      },
      set(val) {
        this.filters.passed = val
        this.push()
      },
    },
    failed: {
      get() {
        return this.$route.query.failed
      },
      set(val) {
        this.filters.failed = val
        this.push()
      },
    },
    ...mapGetters(['stdizerResult', 'isAuthenticated', 'getStructures']),
    protocolId() {
      return this.$route.params.id
    },
    fields() {
      return this.protocol && this.protocol.request ? Object.keys(this.protocol.request.fields) : []
    },
    headers() {
      return [
        { text: '#', value: 'ordinal' },
        {
          text: 'Record ID',
          value: 'rid',
          class: 'position-sticky sticky-header bg-light',
          cellClass: 'position-sticky sticky-cell bg-light',
        },
        { text: '', value: 'type', sortable: false },
        { text: '', value: 'parent', sortable: false },
        { text: 'Original', value: 'original', sortable: false },
        { text: 'Changed', value: 'changed', sortable: false },
        { text: 'Status', value: 'status' },
      ]
        .concat(this.hasIssues ? [{ text: 'Issues', value: 'issues', sortable: false }] : [])
        .concat(this.hasCurationStatus ? [{ text: 'Curation', value: 'curationStatus', sortable: false }] : [])
        .concat(this.hasComment ? [{ text: 'Comment', value: 'comment', sortable: false }] : [])
        .concat(
          this.isAuthenticated && !this.selectable && this.curateMode
            ? [
                {
                  text: 'Actions',
                  value: 'actions',
                  sortable: false,
                  align: 'end',
                  width: '10em',
                },
              ]
            : [],
        )
    },
    STDIZER_URL() {
      return STDIZER_URL
    },
    filteredItems() {
      if (this.protocolId) {
        return this.protocol.records
      } else {
        let records
        if (this.curationFilter.length === 0) records = this.protocol.records
        else if (this.curationFilter.includes('NONE')) records = this.protocol.records.filter((r) => !r.curationStatus)
        else records = this.protocol.records.filter((r) => this.curationFilter.includes(r.curationStatus))

        if (this.hidden && this.components)
          records = records.filter((r) => r.type === 'PARENT' || r.type === 'DUPLICATE' || r.type === 'COMPONENT')
        else {
          if (this.hidden) records = records.filter((r) => r.type === 'PARENT' || r.type === 'DUPLICATE')
          if (this.components) records = records.filter((r) => r.type === 'COMPONENT')
        }

        if (this.changed) records = records.filter((r) => r.parent || r.transformLog.length)

        if (this.failed) records = records.filter((r) => r.status === 'FAILED')
        else if (this.passed) records = records.filter((r) => r.status === 'PASSED')

        if (this.issue && this.issue.length)
          records = records.filter((r) => r.issues.find((i) => i.message === this.issue))

        if (this.change && this.change.length)
          records = records.filter((r) => r.transformLog.find((i) => i.change.value === this.change))

        if (this.search && this.search.length) {
          const re = new RegExp(`.*${this.search}.*`, 'i')
          records = records.filter(
            (r) =>
              re.test(r.original.chemical.cid) ||
              re.test(r.original.chemical.sid) ||
              re.test(r.original.chemical.casrn) ||
              re.test(r.original.chemical.name) ||
              re.test(r.original.chemical.inchi) ||
              re.test(r.original.chemical.inchiKey),
          )
        }

        return records
      }
    },
    hasIssues() {
      return !!this.protocol.records.find((r) => r.issues && r.issues.length)
    },
    hasComment() {
      return !!this.protocol.records.find((r) => r.comment)
    },
    hasCurationStatus() {
      return !!this.protocol.records.find((r) => r.curationStatus)
    },
    countHidden() {
      return this.protocol.hiddenNumber
    },
    countComponents() {
      return this.protocol.componentsNumber
    },
    countChanged() {
      return this.protocol.changedNumber
    },
    countFailed() {
      return this.protocol.failedNumber
    },
    countPassed() {
      return this.protocol.passedNumber
    },
    getStructuresAsMap() {
      return new Map(this.getStructures.map((structure) => [structure.chemical?.inchiKey, structure]))
    },
    stdizerExportItems() {
      return [
        { title: 'Export to SDF', icon: 'mdi-file-download-outline', onClick: () => this.exportFile('SDF') },
        { title: 'Export to CSV', icon: 'mdi-file-delimited-outline', onClick: () => this.exportFile('CSV') },
        { title: 'Export to XLSX', icon: 'mdi-file-excel-outline', onClick: () => this.exportFile('XLSX') },
        { title: 'Export to SMI', icon: 'mdi-file-document-outline', onClick: () => this.exportFile('SMI') },
      ]
    },
  },

  watch: {
    $route: 'getProtocol',
    stdizerResult: {
      handler(result) {
        if (!this.protocolId) {
          this.applyLocalProtocol(result)
        }
      },
      deep: true,
    },
    failed(val) {
      if (val) this.passed = false
    },
    passed(val) {
      if (val) this.failed = false
    },
    curateMode(val) {
      if (!val && this.selectable) this.selectable = false
    },
    options(val) {
      if (this.filters.page !== val.page - 1) this.page = val.page - 1
      if (this.filters.size !== val.itemsPerPage) this.size = val.itemsPerPage
    },
    issue() {
      this.getProtocol()
    },
    change() {
      this.getProtocol()
    },
    search(query) {
      this.query(query)
    },
  },

  methods: {
    query: debounce(function () {
      if (this.protocol) this.getProtocol()
    }, 1000),
    applyLocalProtocol(protocol) {
      this.loading = true
      this.protocol = protocol || { records: [], request: {} }
      this.maxKey = 0

      if (this.protocol?.records) {
        this.protocol.records.forEach((r) => {
          r.key = this.maxKey++
          r.ordinal = this.maxKey
        })

        this.getIssuesAndChanges()
      } else {
        this.issues = []
        this.changes = []
      }

      this.loading = false
    },
    push() {
      const parts = []
      if (this.filters.page) parts.push(`page=${this.filters.page}`)
      if (this.filters.size) parts.push(`size=${this.filters.size}`)
      if (this.filters.hidden) parts.push(`hidden=true`)
      if (this.filters.components) parts.push(`components=true`)
      if (this.filters.changed) parts.push(`changed=true`)
      if (this.filters.passed) parts.push(`passed=true`)
      if (this.filters.failed) parts.push(`failed=true`)

      let baseUrl = '/stdizer'
      if (this.protocolId) {
        baseUrl = `/stdizer/protocols/${this.protocolId}`
      } else if (!this.inlineMode) {
        baseUrl = '/stdizer/protocol'
      }

      const url = baseUrl + (parts.length ? '?' + parts.join('&') : '')
      if (url !== this.$route.fullPath) this.$router.push(url)
    },
    onAclUpdated(acl) {
      this.protocol.acl = acl
    },
    onReset() {
      if (this.protocolId) {
        this.$axios.delete(`${STDIZER_URL}protocols/${this.protocol.id}`).then(() => {
          if (this.protocol.id === this.stdizerResult.id) this.$store.commit('resetStdizerResult')
        })
        this.$router.push('/stdizer/protocols')
      } else {
        this.$store.commit('resetStdizerResult')
        this.applyLocalProtocol(null)
        if (!this.inlineMode) this.$router.push('/stdizer')
      }
    },
    onProtocolCreated(protocol) {
      this.protocol = protocol
      if (this.protocolId || !this.inlineMode) {
        this.$router.push(`/stdizer/protocols/${encodeURIComponent(protocol.id)}`)
      } else {
        this.$store.commit('setStdizerResult', protocol)
      }
    },
    onProtocolUpdated(protocol) {
      this.protocol = protocol
      if (!this.protocolId) this.$store.commit('setStdizerResult', protocol)
    },
    commentRecords(items) {
      this.selected = items
      this.visibleCurate = true
    },
    uncommentRecords(items) {
      if (this.protocolId) {
        this.$axios.patch(`${STDIZER_URL}/protocols/${this.protocolId}`, {
          ordinals: items.map((item) => this.protocol.records.indexOf(item)),
          comment: null,
        })
      }
      items.forEach((r) => {
        r.comment = null
        r.key = this.maxKey++
      })
      if (!this.protocolId) this.$store.commit('setStdizerResult', this.protocol)
    },
    markRecords(items, status) {
      if (this.protocolId) {
        this.$axios.patch(`${STDIZER_URL}/protocols/${this.protocolId}`, {
          ordinals: items.map((item) => this.protocol.records.indexOf(item)),
          curationStatus: status,
        })
      }
      items.forEach((r) => {
        r.curationStatus = status
        r.key = this.maxKey++
      })
      if (!this.protocolId) this.$store.commit('setStdizerResult', this.protocol)
    },
    updateItems(curation) {
      if (this.protocolId) {
        this.$axios.patch(`${STDIZER_URL}/protocols/${this.protocolId}`, {
          ordinals: this.selected.map((r) => this.protocol.records.indexOf(r)),
          comment: curation.comment,
          curationStatus: curation.status,
        })
      }
      this.selected.forEach((r) => {
        r.comment = curation.comment
        r.curationStatus = curation.status
        r.key = this.maxKey++
      })
      if (!this.protocolId) this.$store.commit('setStdizerResult', this.protocol)
    },
    statusFilterChanged(filter) {
      if (filter.length !== 0) {
        if (filter[filter.length - 1] === 'NONE') this.curationFilter = ['NONE']
        else this.curationFilter = filter.filter((f) => f !== 'NONE')
      }
    },
    showTransformLog(item) {
      return () => {
        let transformLog = []
        let parent = item.parent
        if (parent) {
          if (typeof parent === 'number') parent = this.protocol.records.find((r) => parseInt(r['@id']) === parent)
          transformLog = [parent.original, ...parent.transformLog]
          parent.original.parent = true
          item.original.child = true
        }
        this.transformLog = transformLog.concat([item.original, ...item.transformLog])
        this.transformLogVisible = true
      }
    },
    exportFile(format) {
      this.downloading = true
      format = format.toLowerCase()
      if (this.protocolId) {
        this.protocol.records.forEach((record) => {
          record.original.chemical.additionalProps = this.getStructuresAsMap.get(
            record.original.chemical.inchiKey,
          ).chemical.additionalProps
        })
      }
      const url = this.protocolId
        ? `${STDIZER_URL}/protocols/${this.protocolId}/export`
        : `${STDIZER_URL}/protocol/export`
      this.$axios
        .post(
          url,
          {
            protocol: this.protocolId ? null : this.protocol,
            rids: this.selectable ? this.selected.map((r) => r.rid) : [],
            hidden: this.filters.hidden,
            components: this.filters.components,
            changed: this.filters.changed,
            passed: this.filters.passed,
            failed: this.filters.failed,
            issue: this.issue,
            change: this.change,
            query: this.search,
            curationFilter: this.curationFilter,
            format: format.toUpperCase(),
          },
          { responseType: 'arraybuffer' },
        )
        .then((response) => {
          download(getDatedFilename('stdizer', format), response.data, format)
          this.downloading = false
        })
        .catch((error) => {
          console.error('Error during export:', error)
          this.downloading = false
          showErrorToast(error, { title: 'Error during export' })
        })
    },
    getProtocol() {
      if (this.protocolId) {
        this.loading = true
        this.maxKey = 0
        const url = `${STDIZER_URL}/protocols/${this.protocolId}?page=${this.filters.page}&size=${this.filters.size}`
        this.$axios
          .post(url, {
            hidden: this.filters.hidden,
            components: this.filters.components,
            changed: this.filters.changed,
            passed: this.filters.passed,
            failed: this.filters.failed,
            issue: this.issue,
            change: this.change,
            query: this.search,
          })
          .then((response) => {
            if (this.protocol.records.length === 0 && this.protocol.filteredNumber > 0 && this.filters.page > 0)
              this.page = Math.floor(this.protocol.filteredNumber / this.filters.size)

            response.data.records.forEach((r) => {
              r.key = this.maxKey++
              r.ordinal = this.maxKey
              r.ordinal += this.filters.page * this.filters.size
            })

            this.protocol = response.data
            this.loading = false
          })
          .catch(() => {
            this.loading = false
          })
      }
    },
    getIssues() {
      this.issuesLoading = true
      this.$axios
        .get(`${STDIZER_URL}/protocols/${this.protocolId}/issues`)
        .then((response) => {
          this.issues = ['', ...response.data]
          this.issuesLoading = false
        })
        .catch(() => (this.issuesLoading = false))
    },
    getChanges() {
      this.changesLoading = true
      this.$axios
        .get(`${STDIZER_URL}/protocols/${this.protocolId}/changes`)
        .then((response) => {
          this.changes = ['', ...response.data]
          this.changesLoading = false
        })
        .catch(() => (this.changesLoading = false))
    },
    getIssuesAndChanges() {
      const issues = new Set()
      this.protocol.records.forEach((r) => r.issues.forEach((i) => issues.add(i.message)))
      this.issues = ['', ...issues].sort()

      const changes = new Set()
      this.protocol.records.forEach((r) => r.transformLog.forEach((t) => changes.add(t.change.value)))
      this.changes = ['', ...changes].sort()
    },
  },

  mounted() {
    this.filters.page = this.page
    this.filters.size = this.size
    this.filters.hidden = this.hidden
    this.filters.components = this.components
    this.filters.changed = this.changed
    this.filters.passed = this.passed
    this.filters.failed = this.failed

    if (this.protocolId) {
      this.getProtocol()
      this.getIssues()
      this.getChanges()
    } else {
      this.applyLocalProtocol(this.stdizerResult)
    }

    if (this.inlineMode) {
      this.$emit('ready', this)
    }
  },
}
</script>
